import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TextButton, Popover } from '@oneaudi/unified-web-components';
import { UeElement } from '@oneaudi/falcon-tools';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { Context } from '../../../context/Context';
import { useTrackingManager } from '../../../utils/useTrackingManager';
import VtpButtons from './VtpButtons';
import VtpLinks from './VtpLinks';
import { attachVerticalNavigation, getKeyboardFocusableElements } from '../../../utils/a11y';

export const PopoverWrapper = styled.div<{
  isMobile: boolean;
}>`
  width: ${(props) => (props.isMobile ? '100%' : '324px')};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

export const TriggerButton = styled(TextButton)`
  white-space: nowrap;
`;

interface VtpPopoverProps {
  disabled: boolean;
}

const VtpPopover: React.FC<VtpPopoverProps> = ({ disabled }) => {
  const { setPopOverState, isPopOverOpen, content } = useContext(Context);
  const { secondaryNavigation } = useTrackingManager();
  const buttonText = content?.secondaryCta?.shopNowCta?.text;
  const targetRef = useRef(null);

  const lgScreenSize = useMediaQuery('(min-width: 1024px)');
  const textLinkSize: 'sm' | 'md' = process.env.IS_LEGACY && lgScreenSize ? 'md' : 'sm';
  const isMobile = useMediaQuery('(max-width: 767px)');
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!popoverRef.current || !isPopOverOpen) return undefined;
    const cleanup = attachVerticalNavigation(popoverRef.current);

    return () => cleanup();
  }, [isPopOverOpen, popoverRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isPopOverOpen) {
        setPopOverState(true);
        setTimeout(() => {
          if (!popoverRef.current) return;
          const ctas = getKeyboardFocusableElements(popoverRef.current);
          if (ctas.length > 0) {
            ctas[0].focus();
          }
        }, 0);
      }
    }
  };

  return (
    <Popover
      id="vtp-popover"
      role="group"
      withTip={false}
      alignment="right"
      open={isPopOverOpen}
      onClose={() => setPopOverState(false)}
      triggerElement={
        <TriggerButton
          data-testid="popover-trigger-button"
          variant="secondary"
          icon={isPopOverOpen ? 'arrow-up' : 'arrow-down'}
          iconPosition="trailing"
          size={textLinkSize}
          isFullWidth
          onClick={() => {
            setPopOverState(!isPopOverOpen);
            secondaryNavigation(!isPopOverOpen, buttonText);
          }}
          ref={targetRef}
          disabled={disabled}
          onKeyDown={(e) => handleKeyDown(e)}
          aria-haspopup="menu"
        >
          <UeElement
            type="text"
            property="secondaryCta_shopNowCta_text"
            label="Secondary CTA (optional) -  Fill in for VTP Secondary CTA (optional) - Label"
          >
            {buttonText}
          </UeElement>
        </TriggerButton>
      }
      content={
        <PopoverWrapper isMobile={isMobile}>
          <div ref={popoverRef}>{isMobile ? <VtpButtons /> : <VtpLinks />}</div>
        </PopoverWrapper>
      }
    />
  );
};

export default VtpPopover;
